import type { TaskLocationFormatOptions, TaskLocationValue, TaskUtils } from './types';
import { warnSdkUtilFallback } from './warn';

/**
 * A location sub-value as displayable text. Workflow step inputs can bind any sub-key to a
 * resolver object instead of a literal (e.g. `line1: {"$type": "workflow.step", ...}` from a
 * compiled workflow), so a value typed `string | null` may really be an object — render those
 * as empty rather than crashing string handling or printing "[object Object]".
 */
export function locationFieldText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function isLocationValue(value: unknown): value is TaskLocationValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('line1' in value || 'place_id' in value || 'point' in value)
  );
}

function streetLine(loc: TaskLocationValue): string {
  return [locationFieldText(loc.line1), locationFieldText(loc.line2)].filter(Boolean).join(', ');
}

function localityLine(loc: TaskLocationValue): string {
  const statePostal = [locationFieldText(loc.state), locationFieldText(loc.postal_code)]
    .filter(Boolean)
    .join(' ');

  return [locationFieldText(loc.city), statePostal].filter(Boolean).join(', ');
}

/** The address as separate lines, led by an establishment name when the place has one. */
export function buildAddressLines(loc: TaskLocationValue): string[] {
  return [locationFieldText(loc.display_name), streetLine(loc), localityLine(loc)].filter(Boolean);
}

/** The whole postal address on one line, without the establishment name. */
export function formatLocationAddress(loc: TaskLocationValue | null | undefined): string {
  if (!loc) {
    return '';
  }

  return [streetLine(loc), localityLine(loc), locationFieldText(loc.country)]
    .filter(Boolean)
    .join(', ');
}

export function formatLocation(value: unknown, options?: TaskLocationFormatOptions): string | null {
  if (!isLocationValue(value)) {
    warnSdkUtilFallback('location.format', 'The value is not a location; returning null.');
    return null;
  }

  const format = options?.format ?? 'label';
  const address =
    (format === 'label' ? locationFieldText(value.display_name) : '') ||
    formatLocationAddress(value);

  if (!address) {
    warnSdkUtilFallback('location.format', 'The location has no address details; returning null.');
    return null;
  }

  return address;
}

export const locationUtils: TaskUtils['location'] = {
  format: formatLocation
};
